import {
  Datatype,
  ReferencePathDirection,
  ReferenceType,
  type IReference,
  type IReferencePath,
} from '@framework/engine';
import { getCandidateColumns } from '../candidate/table/CandidateColumns';
import {
  electoralListChooserCandidateFieldName,
  electoralListChooserColumnsFieldName,
  electoralListChooserListNameFieldName,
} from './createElectoralListChooserFormSchema';
import type { IElectoralListChooserWidget } from './IElectoralListChooserWidget';

/**
 * Riferimenti ai sotto-campi del candidato scelto nella lista selezionata:
 * - l'id del candidato (`@candidateId`) come valore stringa, in analogia al
 *   widget CTS che espone l'id candidato come reference principale (qui è un
 *   campo fratello, perché la reference principale è la lista);
 * - una reference per colonna configurata (nome, cognome, …), che punta dentro
 *   il sotto-oggetto `@columns` popolato alla selezione;
 * - il nome della lista selezionata (`@listName`), popolato alla selezione
 *   (#57760);
 * - l'id della lista selezionata, che rilegge il campo principale del widget
 *   (già valorizzato con l'id lista) come stringa semplice, senza le opzioni
 *   della reference principale (#58817).
 *
 * Permette alle espressioni di leggere il candidato votato (id e dati) e la
 * lista votata (nome e id).
 */
export function getElectoralListChooserSiblingReferences(
  path: IReferencePath,
  widget: IElectoralListChooserWidget,
): IReference[] {
  // Id del candidato selezionato: campo fratello "piatto" (non annidato in
  // `@columns`), quindi referenziato direttamente per `code` come il campo
  // lista, senza un passo `Child`.
  const candidateIdReference: IReference = {
    type: ReferenceType.Widget,
    widgetType: null,
    id: `${widget.id}-candidateId`,
    code: electoralListChooserCandidateFieldName(widget.code),
    path,
    field: { type: Datatype.String },
    label: `${widget.code}: ID Candidato`,
  };

  // Nome della lista selezionata: come l'id candidato è un campo fratello
  // "piatto", referenziato direttamente per `code` senza un passo `Child`.
  const listNameReference: IReference = {
    type: ReferenceType.Widget,
    widgetType: null,
    id: `${widget.id}-listName`,
    code: electoralListChooserListNameFieldName(widget.code),
    path,
    field: { type: Datatype.String },
    label: `${widget.code}: Nome Lista`,
  };

  const listIdReference: IReference = {
    type: ReferenceType.Widget,
    widgetType: null,
    id: `${widget.id}-listId`,
    code: widget.code,
    path,
    field: { type: Datatype.String },
    label: `${widget.code}: ID Lista`,
  };

  const columnReferences = getCandidateColumns(widget.columns).map(
    column => ({
      type: ReferenceType.Widget,
      widgetType: null,
      id: `${widget.id}-${column.name}`,
      code: column.name,
      path: [
        ...path,
        {
          direction: ReferencePathDirection.Child,
          code: electoralListChooserColumnsFieldName(widget.code),
        },
      ],
      field: { type: column.type },
      label: `${widget.code}: ${column.label}`,
    }),
  ) as IReference[];

  return [
    candidateIdReference,
    listNameReference,
    listIdReference,
    ...columnReferences,
  ];
}
